import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, type User } from '../store/authStore';
import client from '../api/client';
import {
  PolicyStartDateField,
  todayIsoDate,
  validatePolicyStartDate,
} from '../components/PolicyStartDateField';
import { buildRentersInsuranceRequestPayload } from '../utils/rentersInsurancePayload';
import { trackGenerateLead } from '../utils/analytics';
import {
  clearLeadSession,
  readLeadIdFromSession,
  wasLeadConversionFired,
} from '../utils/leadSession';

function InsuranceFormPage() {
  const navigate = useNavigate();
  const { isAuthenticated, setUser } = useAuthStore();

  const refreshUserFromProfile = async () => {
    try {
      const { data } = await client.get<{ user: User }>('/users/profile');
      const cur = useAuthStore.getState().user;
      if (cur) setUser({ ...cur, ...data.user });
    } catch {
      /* ignore */
    }
  };

  const [formData, setFormData] = useState({
    // Basic info from qualification
    insuranceType: sessionStorage.getItem('insuranceType') || 'renters',
    zipCode: sessionStorage.getItem('zipCode') || '',

    // Address
    streetAddress: '',
    addressLine2: '',
    city: 'Houston',
    state: 'Texas',
    country: 'United States',

    // Safety features
    safetyFeatures: [] as string[],

    // Coverage details
    personalLiabilityLimit: '',

    // Building info
    buildingType: '',
    hasExistingRentersInsurance: '',
    hasRoommates: '',

    // Household details
    roommateCount: '',

    // High-value items
    hasHighValueItems: '',

    // Referral (e.g. property compliance landing pages)
    referralCode: sessionStorage.getItem('referralCode') || '',

    policyStartDate: todayIsoDate(),
  });

  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [showLiabilityHelp, setShowLiabilityHelp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isPhone, setIsPhone] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  // If zip code is missing, redirect back to qualification
  useEffect(() => {
    if (!formData.zipCode) {
      navigate('/qualify');
    }
  }, [formData.zipCode, navigate]);

  // Prefill address from account profile when logged in (e.g. after car insurance saved home address).
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get<{ user: User }>('/users/profile');
        if (cancelled) return;
        const p = data.user;
        setFormData((prev) => ({
          ...prev,
          streetAddress: prev.streetAddress || p.address || '',
          addressLine2: prev.addressLine2,
          city: p.city || prev.city,
          state: p.state || prev.state,
          zipCode: prev.zipCode || p.zipCode || '',
        }));
        const cur = useAuthStore.getState().user;
        if (cur) setUser({ ...cur, ...data.user });
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, setUser]);

  // Keep phone/orientation flags in sync for responsive mobile layout.
  useEffect(() => {
    const updateViewportFlags = () => {
      const phoneMedia = window.matchMedia('(max-width: 900px) and (pointer: coarse)');
      const landscapeMedia = window.matchMedia('(orientation: landscape)');
      setIsPhone(phoneMedia.matches);
      setIsLandscape(landscapeMedia.matches);
    };

    updateViewportFlags();
    window.addEventListener('resize', updateViewportFlags);
    window.addEventListener('orientationchange', updateViewportFlags);

    return () => {
      window.removeEventListener('resize', updateViewportFlags);
      window.removeEventListener('orientationchange', updateViewportFlags);
    };
  }, []);

  const safetyFeaturesOptions = [
    'Fire Extinguisher',
    'Smoke Detector',
    'Fire Alarm',
    'Central Reporting Fire Alarm',
    'Sprinklers',
    'Burglar Alarm',
    'Police Connected Burglar Alarm',
    'Doorman',
    'Smart Water Valve',
  ];

  const buildingTypes = [
    'Single family',
    'Multifamily',
    'Apartment',
    'Townhouse',
    'Manufactured/Mobile home',
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSafetyFeatureChange = (feature: string) => {
    setFormData({
      ...formData,
      safetyFeatures: formData.safetyFeatures.includes(feature)
        ? formData.safetyFeatures.filter((f) => f !== feature)
        : [...formData.safetyFeatures, feature],
    });
  };

  const handleNextStep = () => {
    setError('');

    // Basic front-end validation for step 1 fields
    if (!formData.streetAddress || !formData.city || !formData.state || !formData.zipCode || !formData.country) {
      setError('Please complete your full address before continuing.');
      return;
    }

    if (!formData.personalLiabilityLimit) {
      setError('Please choose a personal liability limit before continuing.');
      return;
    }

    if (formData.roommateCount === '') {
      setError('Please tell us how many roommates you have. Enter 0 if you live alone.');
      return;
    }

    setCurrentStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const policyStartErr = validatePolicyStartDate(formData.policyStartDate);
    if (policyStartErr) {
      setError(policyStartErr);
      return;
    }

    if (!isAuthenticated) {
      // User not logged in - store form data and redirect to signup
      const leadId = readLeadIdFromSession();
      sessionStorage.setItem(
        'insuranceFormData',
        JSON.stringify(leadId != null ? { ...formData, leadId } : formData)
      );
      sessionStorage.setItem('insuranceType', 'renters');
      sessionStorage.setItem('redirectAfterSignup', '/insurance/requests');
      navigate('/register');
      return;
    }

    // User is logged in - submit the form
    setLoading(true);

    try {
      const leadId = readLeadIdFromSession();
      await client.post(
        '/insurance/request',
        buildRentersInsuranceRequestPayload(
          leadId != null ? { ...formData, leadId } : formData
        )
      );

      if (!wasLeadConversionFired()) {
        await trackGenerateLead('renters');
      }

      await refreshUserFromProfile();

      // Clear session storage
      sessionStorage.removeItem('insuranceType');
      sessionStorage.removeItem('zipCode');
      sessionStorage.removeItem('referralCode');
      sessionStorage.removeItem('insuranceFormData');
      clearLeadSession();

      navigate('/insurance/requests');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit insurance request');
    } finally {
      setLoading(false);
    }
  };

  const twoColumnGridStyle = {
    display: 'grid',
    gridTemplateColumns: isPhone && !isLandscape ? '1fr' : '1fr 1fr',
    gap: isPhone ? '0.75rem' : '1rem',
  };

  const actionButtonsGridStyle = {
    display: 'grid',
    gridTemplateColumns: isPhone && !isLandscape ? '1fr' : '1fr 1fr',
    gap: isPhone ? '0.75rem' : '1rem',
    marginTop: '2rem',
  };

  const radioGroupStyle = {
    display: 'flex',
    flexDirection: isPhone && !isLandscape ? ('column' as const) : ('row' as const),
    gap: isPhone ? '0.85rem' : '2rem',
    marginTop: '0.5rem',
  };

  return (
    <div
      className="container"
      style={{
        maxWidth: isPhone ? (isLandscape ? '100%' : '560px') : '700px',
        margin: '0 auto',
        padding: isPhone ? '0.75rem' : undefined,
      }}
    >
      <div className="card" style={{ padding: isPhone ? '1rem' : undefined }}>
        <div className="card-header">
          Complete Your Renters Insurance Application
          <div style={{ fontSize: '0.9rem', marginTop: '0.25rem', color: 'var(--color-text-secondary, #666)' }}>
            Step {currentStep} of 2
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {currentStep === 1 && (
            <>
              {/* Address Section */}
              <h4 style={{ marginTop: '2rem', marginBottom: '1rem', color: 'var(--color-green)' }}>
                Address Information
              </h4>

              <div className="form-group">
                <label htmlFor="streetAddress">Street Address *</label>
                <input
                  id="streetAddress"
                  type="text"
                  name="streetAddress"
                  value={formData.streetAddress}
                  onChange={handleChange}
                  placeholder="123 Main Street"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="addressLine2">Address Line 2 (Apt, Suite, etc.)</label>
                <input
                  id="addressLine2"
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  placeholder="Apartment 4B"
                />
              </div>

              <div style={twoColumnGridStyle}>
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    id="city"
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">State *</label>
                  <input
                    id="state"
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Zip Code */}
              <div className="form-group">
                <label htmlFor="zipCode">ZIP Code *</label>
                <input
                  id="zipCode"
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      // allow only digits and limit to 5 chars
                      zipCode: e.target.value.replace(/\D/g, '').slice(0, 5),
                    })
                  }
                  placeholder="77001"
                  maxLength={5}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">Country *</label>
                <input
                  id="country"
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Personal Liability Limit */}
              <div className="form-group">
                <label htmlFor="personalLiabilityLimit">
                  Personal liability limit desired *
                </label>
                <input
                  id="personalLiabilityLimit"
                  type="number"
                  name="personalLiabilityLimit"
                  value={formData.personalLiabilityLimit}
                  onChange={handleChange}
                  min={0}
                  step={50000}
                  placeholder="Enter 0 if unsure"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowLiabilityHelp((prev) => !prev)}
                  style={{
                    marginTop: '0.5rem',
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    color: 'var(--color-green)',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    textDecoration: 'underline',
                  }}
                >
                  Not sure how much? Click here for a quick guide.
                </button>
                {showLiabilityHelp && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#555' }}>
                    <ul style={{ paddingLeft: '1.2rem', margin: 0 }}>
                      <li style={{ marginBottom: '0.35rem' }}>
                        Your personal liability limit is the maximum amount your policy can pay if you are found legally
                        responsible for accidentally injuring someone or damaging their property.
                      </li>
                      <li>
                        For most renters, $100,000 is a solid starting point. Consider a higher limit if you have
                        significant savings, higher income, or host guests frequently.
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Roommates count */}
              <div className="form-group">
                <label htmlFor="roommateCount">How many roommates do you have? *</label>
                <input
                  id="roommateCount"
                  type="number"
                  name="roommateCount"
                  min={0}
                  step={1}
                  value={formData.roommateCount}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      roommateCount: e.target.value.replace(/\D/g, ''),
                      hasRoommates: Number(e.target.value || 0) > 0 ? 'yes' : 'no',
                    })
                  }
                  placeholder="Enter 0 if you live alone"
                  required
                />
              </div>

              {/* Buttons for step 1 */}
              <div style={actionButtonsGridStyle}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/qualify')}
                >
                  Back
                </button>
                <button type="button" className="btn btn-primary" onClick={handleNextStep}>
                  Continue
                </button>
              </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              <PolicyStartDateField
                label="When do you need your renters policy to start?"
                value={formData.policyStartDate}
                onChange={(date) => setFormData((prev) => ({ ...prev, policyStartDate: date }))}
                id="rentersPolicyStartDate"
              />

              <div className="form-group">
                <label>Safety Features (Select all that apply)</label>
                <div style={twoColumnGridStyle}>
                {safetyFeaturesOptions.map((feature) => (
                  <div key={feature} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      id={feature}
                      checked={formData.safetyFeatures.includes(feature)}
                      onChange={() => handleSafetyFeatureChange(feature)}
                      style={{ cursor: 'pointer' }}
                    />
                    <label
                      htmlFor={feature}
                      style={{ margin: 0, cursor: 'pointer', fontSize: '0.95rem' }}
                    >
                      {feature}
                    </label>
                  </div>
                ))}
                </div>
              </div>

              {/* Building Type */}
              <div className="form-group">
                <label htmlFor="buildingType">What type of building do you live in? *</label>
                <select
                  id="buildingType"
                  name="buildingType"
                  value={formData.buildingType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select building type...</option>
                  {buildingTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Existing Insurance */}
              <div className="form-group">
                <label>Do you have existing renters insurance? *</label>
                <div style={radioGroupStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      id="existingYes"
                      name="hasExistingRentersInsurance"
                      value="yes"
                      checked={formData.hasExistingRentersInsurance === 'yes'}
                      onChange={handleChange}
                      required
                    />
                    <label htmlFor="existingYes" style={{ margin: 0, cursor: 'pointer' }}>
                      Yes
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      id="existingNo"
                      name="hasExistingRentersInsurance"
                      value="no"
                      checked={formData.hasExistingRentersInsurance === 'no'}
                      onChange={handleChange}
                      required
                    />
                    <label htmlFor="existingNo" style={{ margin: 0, cursor: 'pointer' }}>
                      No
                    </label>
                  </div>
                </div>
              </div>

              {/* High-value items */}
              <div className="form-group">
                <label>
                  Do you own any high-value jewelry, watches, artwork, collectibles, musical instruments, or similar
                  items?
                </label>
                <div style={radioGroupStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      id="highValueYes"
                      name="hasHighValueItems"
                      value="yes"
                      checked={formData.hasHighValueItems === 'yes'}
                      onChange={handleChange}
                      required
                    />
                    <label htmlFor="highValueYes" style={{ margin: 0, cursor: 'pointer' }}>
                      Yes
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      id="highValueNo"
                      name="hasHighValueItems"
                      value="no"
                      checked={formData.hasHighValueItems === 'no'}
                      onChange={handleChange}
                      required
                    />
                    <label htmlFor="highValueNo" style={{ margin: 0, cursor: 'pointer' }}>
                      No
                    </label>
                  </div>
                </div>
              </div>

              {/* Referral Code */}
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label htmlFor="referralCode" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  Referral Code or Referrer's Name
                  <span
                    title="If someone referred you to GetSkimp, enter their email."
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--color-grey)',
                      color: '#4B5563',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      cursor: 'help',
                    }}
                  >
                    ?
                  </span>
                </label>
                <input
                  id="referralCode"
                  type="text"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleChange}
                  placeholder="referrer@email.com"
                />
                <small style={{ color: '#999', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                  Typically your referrer's email
                </small>
              </div>

              {/* Buttons for step 2 */}
              <div style={actionButtonsGridStyle}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCurrentStep(1)}
                >
                  Back
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Processing...' : isAuthenticated ? 'Submit Request' : 'Continue to Sign Up'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

export default InsuranceFormPage;
